if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;

      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;

      abortController = undefined;
      pendingRequestUrl = null;

      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      pubSubRetryCount = 0;
      pubSubRetryTimer = null;
      nativeVariantChangeHandler = null;

      constructor() {
        super();

        this.quantityInput =
          this.querySelector('.quantity__input');
      }

      /* ==========================================================
         CONNECT / DISCONNECT
      ========================================================== */

      connectedCallback() {
        this.initializeProductSwapUtility();

        /*
         * pubsub.js kabhi-kabhi product-info.js ke baad
         * initialize hoti hai. Isliye safe retry use kar rahe hain.
         */
        this.initializeVariantSubscription();

        this.initQuantityHandlers();

        this.dispatchEvent(
          new CustomEvent('product-info:loaded', {
            bubbles: true
          })
        );
      }

      disconnectedCallback() {
        if (
          typeof this.onVariantChangeUnsubscriber ===
          'function'
        ) {
          this.onVariantChangeUnsubscriber();
        }

        if (
          typeof this.cartUpdateUnsubscriber ===
          'function'
        ) {
          this.cartUpdateUnsubscriber();
        }

        if (this.pubSubRetryTimer) {
          clearTimeout(this.pubSubRetryTimer);
          this.pubSubRetryTimer = null;
        }

        if (this.nativeVariantChangeHandler) {
          this.removeEventListener(
            'change',
            this.nativeVariantChangeHandler,
            true
          );

          this.nativeVariantChangeHandler = null;
        }
      }

      /* ==========================================================
         PUBSUB INITIALIZATION
      ========================================================== */

      initializeVariantSubscription() {
        const pubSubAvailable =
          typeof window.subscribe === 'function' ||
          typeof subscribe === 'function';

        const eventsAvailable =
          typeof window.PUB_SUB_EVENTS !== 'undefined' ||
          typeof PUB_SUB_EVENTS !== 'undefined';

        const subscribeFunction =
          typeof window.subscribe === 'function'
            ? window.subscribe
            : typeof subscribe === 'function'
              ? subscribe
              : null;

        const eventObject =
          typeof window.PUB_SUB_EVENTS !== 'undefined'
            ? window.PUB_SUB_EVENTS
            : typeof PUB_SUB_EVENTS !== 'undefined'
              ? PUB_SUB_EVENTS
              : null;

        if (
          pubSubAvailable &&
          eventsAvailable &&
          subscribeFunction &&
          eventObject?.optionValueSelectionChange
        ) {
          if (
            typeof this.onVariantChangeUnsubscriber ===
            'function'
          ) {
            this.onVariantChangeUnsubscriber();
          }

          this.onVariantChangeUnsubscriber =
            subscribeFunction(
              eventObject.optionValueSelectionChange,
              this.handleOptionValueChange.bind(this)
            );

          this.pubSubRetryCount = 0;

          return;
        }

        /*
         * pubsub.js late load ho rahi ho to maximum
         * 20 baar retry karo.
         */
        if (this.pubSubRetryCount < 20) {
          this.pubSubRetryCount += 1;

          this.pubSubRetryTimer = setTimeout(() => {
            this.initializeVariantSubscription();
          }, 250);

          return;
        }

        console.error(
          'ProductInfo: subscribe() or PUB_SUB_EVENTS is unavailable. Check pubsub.js and constants.js loading order.'
        );
      }

      /* ==========================================================
         CALLBACKS
      ========================================================== */

      addPreProcessCallback(callback) {
        if (typeof callback !== 'function') return;

        this.preProcessHtmlCallbacks.push(callback);
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) => {
          if (!html) return;

          html
            .querySelectorAll('.scroll-trigger')
            .forEach((element) => {
              element.classList.add(
                'scroll-trigger--cancel'
              );
            });
        });

        this.postProcessHtmlCallbacks.push(() => {
          window?.Shopify?.PaymentButton?.init?.();
          window?.ProductModel?.loadShopifyXR?.();
        });
      }

      /* ==========================================================
         QUANTITY
      ========================================================== */

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm =
          this.querySelector('.product-form__quantity');

        if (!this.quantityForm) return;

        this.setQuantityBoundries();

        if (this.dataset.originalSection) return;

        const subscribeFunction =
          typeof window.subscribe === 'function'
            ? window.subscribe
            : typeof subscribe === 'function'
              ? subscribe
              : null;

        const eventObject =
          typeof window.PUB_SUB_EVENTS !== 'undefined'
            ? window.PUB_SUB_EVENTS
            : typeof PUB_SUB_EVENTS !== 'undefined'
              ? PUB_SUB_EVENTS
              : null;

        if (
          subscribeFunction &&
          eventObject?.cartUpdate
        ) {
          this.cartUpdateUnsubscriber =
            subscribeFunction(
              eventObject.cartUpdate,
              this.fetchQuantityRules.bind(this)
            );
        }
      }

      /* ==========================================================
         VARIANT CHANGE
      ========================================================== */

      handleOptionValueChange({
        data: {
          event,
          target,
          selectedOptionValues
        }
      }) {
        if (!event?.target) return;
        if (!this.contains(event.target)) return;
        if (!target) return;

        this.resetProductFormState();

        const optionValues = Array.isArray(
          selectedOptionValues
        )
          ? selectedOptionValues
          : [];

        const productUrl =
          target.dataset?.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;

        if (!productUrl) {
          console.error(
            'ProductInfo: Product URL was not found.'
          );

          return;
        }

        this.pendingRequestUrl = productUrl;

        const shouldSwapProduct =
          this.dataset.url !== productUrl;

        const shouldFetchFullPage =
          this.dataset.updateUrl === 'true' &&
          shouldSwapProduct;

        const requestUrl =
          this.buildRequestUrlWithParams(
            productUrl,
            optionValues,
            shouldFetchFullPage
          );

        this.renderProductInfo({
          requestUrl,
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(
                productUrl,
                shouldFetchFullPage
              )
            : this.handleUpdateProductInfo(productUrl)
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;

        if (
          productForm &&
          typeof productForm.toggleSubmitButton ===
            'function'
        ) {
          productForm.toggleSubmitButton(true);
        } else {
          const submitButton =
            this.querySelector(
              '[name="add"], button[type="submit"]'
            );

          if (submitButton) {
            submitButton.disabled = true;

            submitButton.setAttribute(
              'aria-disabled',
              'true'
            );
          }
        }

        if (
          productForm &&
          typeof productForm.handleErrorMessage ===
            'function'
        ) {
          productForm.handleErrorMessage();
        }
      }

      /* ==========================================================
         PRODUCT SWAP
      ========================================================== */

      handleSwapProduct(
        productUrl,
        updateFullPage
      ) {
        return (html) => {
          if (!html) return;

          this.productModal?.remove();

          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : 'product-info';

          const productInfoNode =
            html.querySelector(selector);

          if (!productInfoNode) {
            console.error(
              'ProductInfo: Updated product-info element was not found.'
            );

            return;
          }

          const variant =
            this.getSelectedVariant(productInfoNode);

          this.updateURL(
            productUrl,
            variant?.id
          );

          if (updateFullPage) {
            const newTitle =
              html.querySelector('head title');

            const currentTitle =
              document.querySelector('head title');

            if (newTitle && currentTitle) {
              currentTitle.innerHTML =
                newTitle.innerHTML;
            }

            const currentMain =
              document.querySelector('main');

            const newMain =
              html.querySelector('main');

            if (
              currentMain &&
              newMain &&
              typeof HTMLUpdateUtility !==
                'undefined'
            ) {
              HTMLUpdateUtility.viewTransition(
                currentMain,
                newMain,
                this.preProcessHtmlCallbacks,
                this.postProcessHtmlCallbacks
              );
            }

            return;
          }

          const newProductInfo =
            html.querySelector('product-info');

          if (
            newProductInfo &&
            typeof HTMLUpdateUtility !==
              'undefined'
          ) {
            HTMLUpdateUtility.viewTransition(
              this,
              newProductInfo,
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      /* ==========================================================
         FETCH UPDATED PRODUCT SECTION
      ========================================================== */

      renderProductInfo({
        requestUrl,
        targetId,
        callback
      }) {
        this.abortController?.abort();

        this.abortController =
          new AbortController();

        fetch(requestUrl, {
          signal: this.abortController.signal
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(
                `Product section request failed: ${response.status}`
              );
            }

            return response.text();
          })
          .then((responseText) => {
            this.pendingRequestUrl = null;

            const html =
              new DOMParser().parseFromString(
                responseText,
                'text/html'
              );

            if (typeof callback === 'function') {
              callback(html);
            }
          })
          .then(() => {
            if (!targetId) return;

            document
              .querySelector(
                `#${this.escapeSelector(targetId)}`
              )
              ?.focus?.({
                preventScroll: true
              });
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log(
                'Product variant request aborted.'
              );

              return;
            }

            console.error(
              'ProductInfo request error:',
              error
            );
          });
      }

      escapeSelector(value) {
        if (
          window.CSS &&
          typeof window.CSS.escape === 'function'
        ) {
          return window.CSS.escape(String(value));
        }

        return String(value).replace(
          /(["\\])/g,
          '\\$1'
        );
      }

      getSelectedVariant(productInfoNode) {
        if (!productInfoNode) return null;

        const selectedVariantElement =
          productInfoNode.querySelector(
            'variant-selects [data-selected-variant]'
          );

        const selectedVariant =
          selectedVariantElement?.innerHTML?.trim();

        if (!selectedVariant) return null;

        try {
          return JSON.parse(selectedVariant);
        } catch (error) {
          console.error(
            'ProductInfo: Selected variant JSON parse error:',
            error
          );

          return null;
        }
      }

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false
      ) {
        const params = [];

        if (!shouldFetchFullPage) {
          params.push(
            `section_id=${encodeURIComponent(
              this.sectionId
            )}`
          );
        }

        if (
          Array.isArray(optionValues) &&
          optionValues.length
        ) {
          params.push(
            `option_values=${optionValues
              .map((value) =>
                encodeURIComponent(value)
              )
              .join(',')}`
          );
        }

        const separator =
          url.includes('?') ? '&' : '?';

        return params.length
          ? `${url}${separator}${params.join('&')}`
          : url;
      }

      /* ==========================================================
         OPTION VALUES
      ========================================================== */

      updateOptionValues(html) {
        if (!html) return;

        const newVariantSelects =
          html.querySelector('variant-selects');

        const currentVariantSelects =
          this.variantSelectors;

        if (
          !newVariantSelects ||
          !currentVariantSelects
        ) {
          return;
        }

        if (
          typeof HTMLUpdateUtility !==
          'undefined'
        ) {
          HTMLUpdateUtility.viewTransition(
            currentVariantSelects,
            newVariantSelects,
            this.preProcessHtmlCallbacks
          );

          return;
        }

        currentVariantSelects.innerHTML =
          newVariantSelects.innerHTML;
      }

      /* ==========================================================
         UPDATE PRODUCT INFO
      ========================================================== */

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          if (!html) return;

          const variant =
            this.getSelectedVariant(html);

          this.pickupAvailability?.update?.(
            variant
          );

          this.updateOptionValues(html);

          this.updateURL(
            productUrl,
            variant?.id
          );

          this.updateVariantInputs(
            variant?.id
          );

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(
            html,
            variant?.featured_media?.id
          );

          const updateSourceFromDestination = (
            id,
            shouldHide = () => false
          ) => {
            const source =
              html.getElementById(
                `${id}-${this.sectionId}`
              );

            const destination =
              this.querySelector(
                `#${id}-${this.dataset.section}`
              );

            if (!source || !destination) return;

            destination.innerHTML =
              source.innerHTML;

            destination.classList.toggle(
              'hidden',
              shouldHide(source)
            );
          };

          updateSourceFromDestination('price');

          updateSourceFromDestination(
            'Sku',
            ({ classList }) =>
              classList.contains('hidden')
          );

          updateSourceFromDestination(
            'Inventory',
            ({ innerText }) =>
              innerText.trim() === ''
          );

          updateSourceFromDestination('Volume');

          updateSourceFromDestination(
            'Price-Per-Item',
            ({ classList }) =>
              classList.contains('hidden')
          );

          this.updateQuantityRules(
            this.sectionId,
            html
          );

          this
            .querySelector(
              `#Quantity-Rules-${this.dataset.section}`
            )
            ?.classList.remove('hidden');

          this
            .querySelector(
              `#Volume-Note-${this.dataset.section}`
            )
            ?.classList.remove('hidden');

          const productForm =
            this.productForm;

          const newSubmitButton =
            html.getElementById(
              `ProductSubmitButton-${this.sectionId}`
            );

          const shouldDisableButton =
            newSubmitButton?.hasAttribute(
              'disabled'
            ) ?? true;

          if (
            productForm &&
            typeof productForm.toggleSubmitButton ===
              'function'
          ) {
            productForm.toggleSubmitButton(
              shouldDisableButton,
              window.variantStrings?.soldOut ||
                'Sold out'
            );
          } else {
            this.updateSubmitButtonFallback(
              shouldDisableButton,
              shouldDisableButton
                ? window.variantStrings?.soldOut ||
                    'Sold out'
                : window.variantStrings?.addToCart ||
                    'Add to cart'
            );
          }

          this.publishVariantChange(
            html,
            variant
          );
        };
      }

      publishVariantChange(html, variant) {
        const publishFunction =
          typeof window.publish === 'function'
            ? window.publish
            : typeof publish === 'function'
              ? publish
              : null;

        const eventObject =
          typeof window.PUB_SUB_EVENTS !== 'undefined'
            ? window.PUB_SUB_EVENTS
            : typeof PUB_SUB_EVENTS !== 'undefined'
              ? PUB_SUB_EVENTS
              : null;

        if (
          publishFunction &&
          eventObject?.variantChange
        ) {
          publishFunction(
            eventObject.variantChange,
            {
              data: {
                sectionId: this.sectionId,
                html,
                variant
              }
            }
          );
        }

        /*
         * Custom gallery aur external scripts ke liye
         * native event bhi dispatch kar rahe hain.
         */
        this.dispatchEvent(
          new CustomEvent(
            'product:variant-change',
            {
              bubbles: true,
              detail: {
                sectionId: this.sectionId,
                html,
                variant
              }
            }
          )
        );
      }

      /* ==========================================================
         MEDIA UPDATE
      ========================================================== */

      updateMedia(
        html,
        variantFeaturedMediaId
      ) {
        if (!variantFeaturedMediaId) return;
        if (!html) return;

        const mediaGallery =
          this.querySelector('media-gallery');

        const mediaGallerySource =
          mediaGallery?.querySelector('ul');

        const mediaGalleryDestination =
          html.querySelector(
            'media-gallery ul'
          );

        const refreshSourceData = () => {
          if (
            this.hasAttribute(
              'data-zoom-on-hover'
            ) &&
            typeof enableZoomOnHover ===
              'function'
          ) {
            enableZoomOnHover(2);
          }

          const mediaGallerySourceItems =
            mediaGallerySource
              ? Array.from(
                  mediaGallerySource.querySelectorAll(
                    'li[data-media-id]'
                  )
                )
              : [];

          const sourceSet =
            new Set(
              mediaGallerySourceItems.map(
                (item) =>
                  item.dataset.mediaId
              )
            );

          const sourceMap =
            new Map(
              mediaGallerySourceItems.map(
                (item, index) => [
                  item.dataset.mediaId,
                  {
                    item,
                    index
                  }
                ]
              )
            );

          return [
            mediaGallerySourceItems,
            sourceSet,
            sourceMap
          ];
        };

        if (
          mediaGallerySource &&
          mediaGalleryDestination
        ) {
          let [
            mediaGallerySourceItems,
            sourceSet,
            sourceMap
          ] = refreshSourceData();

          const mediaGalleryDestinationItems =
            Array.from(
              mediaGalleryDestination.querySelectorAll(
                'li[data-media-id]'
              )
            );

          const destinationSet =
            new Set(
              mediaGalleryDestinationItems.map(
                (item) =>
                  item.dataset.mediaId
              )
            );

          let shouldRefresh = false;

          for (
            let index =
              mediaGalleryDestinationItems.length -
              1;
            index >= 0;
            index--
          ) {
            const destinationItem =
              mediaGalleryDestinationItems[index];

            if (
              !sourceSet.has(
                destinationItem.dataset.mediaId
              )
            ) {
              mediaGallerySource.prepend(
                destinationItem
              );

              shouldRefresh = true;
            }
          }

          for (
            let index = 0;
            index <
            mediaGallerySourceItems.length;
            index++
          ) {
            const sourceItem =
              mediaGallerySourceItems[index];

            if (
              !destinationSet.has(
                sourceItem.dataset.mediaId
              )
            ) {
              sourceItem.remove();
              shouldRefresh = true;
            }
          }

          if (shouldRefresh) {
            [
              mediaGallerySourceItems,
              sourceSet,
              sourceMap
            ] = refreshSourceData();
          }

          mediaGalleryDestinationItems.forEach(
            (
              destinationItem,
              destinationIndex
            ) => {
              const sourceData =
                sourceMap.get(
                  destinationItem.dataset.mediaId
                );

              if (
                !sourceData ||
                sourceData.index ===
                  destinationIndex
              ) {
                return;
              }

              const referenceItem =
                mediaGallerySource.querySelector(
                  `li:nth-of-type(${
                    destinationIndex + 1
                  })`
                );

              mediaGallerySource.insertBefore(
                sourceData.item,
                referenceItem || null
              );

              [
                mediaGallerySourceItems,
                sourceSet,
                sourceMap
              ] = refreshSourceData();
            }
          );
        }

        /*
         * Dawn default gallery active media.
         */
        mediaGallery?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        /*
         * Custom gallery ko exact selected variant
         * featured media ID bhejo.
         */
        this.dispatchEvent(
          new CustomEvent(
            'custom-gallery:variant-media',
            {
              bubbles: true,
              detail: {
                mediaId: String(
                  variantFeaturedMediaId
                )
              }
            }
          )
        );

        /*
         * Color filter mein Style radio ko Color samajhne
         * wala issue fix kiya hai.
         */
        setTimeout(() => {
          const currentMediaGallery =
            this.querySelector('media-gallery');

          if (!currentMediaGallery) return;

          const colorName =
            this.getSelectedColorValue();

          if (
            colorName &&
            typeof currentMediaGallery.filterByColor ===
              'function'
          ) {
            currentMediaGallery.filterByColor(
              colorName
            );
          }
        }, 150);

        /*
         * Product modal update.
         */
        const modalContent =
          this.productModal?.querySelector(
            '.product-media-modal__content'
          );

        const newModalContent =
          html.querySelector(
            'product-modal .product-media-modal__content'
          );

        if (
          modalContent &&
          newModalContent
        ) {
          modalContent.innerHTML =
            newModalContent.innerHTML;
        }
      }

      getSelectedColorValue() {
        const variantSelects =
          this.variantSelectors;

        if (!variantSelects) return '';

        const groups =
          variantSelects.querySelectorAll(
            'fieldset, .product-form__input'
          );

        for (const group of groups) {
          const label =
            group.querySelector(
              'legend, .form__label'
            );

          const groupName = (
            group.dataset.optionName ||
            group.dataset.name ||
            label?.textContent ||
            ''
          )
            .trim()
            .toLowerCase();

          const isColorGroup =
            groupName.includes('color') ||
            groupName.includes('colour');

          if (!isColorGroup) continue;

          const checkedInput =
            group.querySelector(
              'input[type="radio"]:checked'
            );

          if (checkedInput) {
            return (
              checkedInput.dataset.variantTitle ||
              checkedInput.value ||
              ''
            ).trim();
          }

          const select =
            group.querySelector('select');

          if (select) {
            const selectedOption =
              select.options[
                select.selectedIndex
              ];

            return (
              selectedOption?.dataset
                ?.variantTitle ||
              selectedOption?.dataset?.value ||
              selectedOption?.value ||
              selectedOption?.textContent ||
              ''
            ).trim();
          }
        }

        return '';
      }

      /* ==========================================================
         VARIANT INPUT
      ========================================================== */

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          [
            `#product-form-${this.dataset.section}`,
            `#product-form-installment-${this.dataset.section}`
          ].join(', ')
        ).forEach((productForm) => {
          const input =
            productForm.querySelector(
              'input[name="id"]'
            );

          if (!input) return;

          input.value = variantId ?? '';

          input.dispatchEvent(
            new Event('change', {
              bubbles: true
            })
          );
        });
      }

      /* ==========================================================
         URL
      ========================================================== */

      updateURL(url, variantId) {
        if (!url) return;

        this
          .querySelector('share-button')
          ?.updateUrl?.(
            `${window.shopUrl || ''}${url}${
              variantId
                ? `?variant=${variantId}`
                : ''
            }`
          );

        if (
          this.dataset.updateUrl === 'false'
        ) {
          return;
        }

        window.history.replaceState(
          {},
          '',
          `${url}${
            variantId
              ? `?variant=${variantId}`
              : ''
          }`
        );
      }

      /* ==========================================================
         UNAVAILABLE
      ========================================================== */

      setUnavailable() {
        const productForm =
          this.productForm;

        const unavailableText =
          window.variantStrings?.unavailable ||
          'Unavailable';

        if (
          productForm &&
          typeof productForm.toggleSubmitButton ===
            'function'
        ) {
          productForm.toggleSubmitButton(
            true,
            unavailableText
          );
        } else {
          this.updateSubmitButtonFallback(
            true,
            unavailableText
          );
        }

        const selectors = [
          'price',
          'Inventory',
          'Sku',
          'Price-Per-Item',
          'Volume-Note',
          'Volume',
          'Quantity-Rules'
        ]
          .map(
            (id) =>
              `#${id}-${this.dataset.section}`
          )
          .join(', ');

        this
          .querySelectorAll(selectors)
          .forEach((element) => {
            element.classList.add('hidden');
          });
      }

      updateSubmitButtonFallback(
        disabled,
        text
      ) {
        const submitButton =
          this.querySelector(
            `#ProductSubmitButton-${this.dataset.section}`
          ) ||
          this.querySelector(
            '[name="add"], button[type="submit"]'
          );

        if (!submitButton) return;

        submitButton.disabled =
          Boolean(disabled);

        submitButton.setAttribute(
          'aria-disabled',
          disabled ? 'true' : 'false'
        );

        const buttonText =
          submitButton.querySelector(
            'span:not(.loading__spinner)'
          );

        if (buttonText && text) {
          buttonText.textContent = text;
        }
      }

      /* ==========================================================
         QUANTITY BOUNDARIES
      ========================================================== */

      setQuantityBoundries() {
        if (!this.quantityInput) return;

        const cartQuantity =
          this.quantityInput.dataset
            .cartQuantity
            ? parseInt(
                this.quantityInput.dataset
                  .cartQuantity,
                10
              )
            : 0;

        const minimum =
          this.quantityInput.dataset.min
            ? parseInt(
                this.quantityInput.dataset.min,
                10
              )
            : 1;

        const maximum =
          this.quantityInput.dataset.max
            ? parseInt(
                this.quantityInput.dataset.max,
                10
              )
            : null;

        const step =
          this.quantityInput.step
            ? parseInt(
                this.quantityInput.step,
                10
              )
            : 1;

        let calculatedMinimum = minimum;

        const calculatedMaximum =
          maximum === null
            ? null
            : maximum - cartQuantity;

        if (calculatedMaximum !== null) {
          calculatedMinimum = Math.min(
            calculatedMinimum,
            calculatedMaximum
          );
        }

        if (cartQuantity >= minimum) {
          calculatedMinimum = Math.min(
            calculatedMinimum,
            step
          );
        }

        this.quantityInput.min =
          Math.max(calculatedMinimum, 1);

        if (
          calculatedMaximum !== null &&
          calculatedMaximum > 0
        ) {
          this.quantityInput.max =
            calculatedMaximum;
        } else {
          this.quantityInput.removeAttribute(
            'max'
          );
        }

        this.quantityInput.value =
          this.quantityInput.min;

        const publishFunction =
          typeof window.publish === 'function'
            ? window.publish
            : typeof publish === 'function'
              ? publish
              : null;

        const eventObject =
          typeof window.PUB_SUB_EVENTS !== 'undefined'
            ? window.PUB_SUB_EVENTS
            : typeof PUB_SUB_EVENTS !== 'undefined'
              ? PUB_SUB_EVENTS
              : null;

        if (
          publishFunction &&
          eventObject?.quantityUpdate
        ) {
          publishFunction(
            eventObject.quantityUpdate,
            undefined
          );
        }
      }

      fetchQuantityRules() {
        const currentVariantId =
          this.productForm?.variantIdInput
            ?.value;

        if (!currentVariantId) return;
        if (!this.dataset.url) return;

        const spinner =
          this.querySelector(
            '.quantity__rules-cart .loading__spinner'
          );

        spinner?.classList.remove('hidden');

        fetch(
          `${this.dataset.url}?variant=${encodeURIComponent(
            currentVariantId
          )}&section_id=${encodeURIComponent(
            this.dataset.section
          )}`
        )
          .then((response) => {
            if (!response.ok) {
              throw new Error(
                `Quantity rules request failed: ${response.status}`
              );
            }

            return response.text();
          })
          .then((responseText) => {
            const html =
              new DOMParser().parseFromString(
                responseText,
                'text/html'
              );

            this.updateQuantityRules(
              this.dataset.section,
              html
            );
          })
          .catch((error) => {
            console.error(
              'Quantity rules error:',
              error
            );
          })
          .finally(() => {
            spinner?.classList.add('hidden');
          });
      }

      updateQuantityRules(
        sectionId,
        html
      ) {
        if (!this.quantityInput) return;
        if (!this.quantityForm) return;
        if (!html) return;

        const quantityFormUpdated =
          html.getElementById(
            `Quantity-Form-${sectionId}`
          );

        if (!quantityFormUpdated) return;

        const selectors = [
          '.quantity__input',
          '.quantity__rules',
          '.quantity__label'
        ];

        selectors.forEach((selector) => {
          const current =
            this.quantityForm.querySelector(
              selector
            );

          const updated =
            quantityFormUpdated.querySelector(
              selector
            );

          if (!current || !updated) return;

          if (
            selector === '.quantity__input'
          ) {
            const attributes = [
              'data-cart-quantity',
              'data-min',
              'data-max',
              'step'
            ];

            attributes.forEach(
              (attribute) => {
                const updatedValue =
                  updated.getAttribute(
                    attribute
                  );

                if (updatedValue !== null) {
                  current.setAttribute(
                    attribute,
                    updatedValue
                  );
                } else {
                  current.removeAttribute(
                    attribute
                  );
                }
              }
            );

            return;
          }

          current.innerHTML =
            updated.innerHTML;
        });

        this.setQuantityBoundries();
      }

      /* ==========================================================
         GETTERS
      ========================================================== */

      get productForm() {
        return this.querySelector(
          'product-form'
        );
      }

      get productModal() {
        return document.querySelector(
          `#ProductModal-${this.dataset.section}`
        );
      }

      get pickupAvailability() {
        return this.querySelector(
          'pickup-availability'
        );
      }

      get variantSelectors() {
        return this.querySelector(
          'variant-selects'
        );
      }

      get relatedProducts() {
        if (
          typeof SectionId === 'undefined'
        ) {
          return null;
        }

        const relatedProductsSectionId =
          SectionId.getIdForSection(
            SectionId.parseId(
              this.sectionId
            ),
            'related-products'
          );

        return document.querySelector(
          `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`
        );
      }

      get quickOrderList() {
        if (
          typeof SectionId === 'undefined'
        ) {
          return null;
        }

        const quickOrderListSectionId =
          SectionId.getIdForSection(
            SectionId.parseId(
              this.sectionId
            ),
            'quick_order_list'
          );

        return document.querySelector(
          `quick-order-list[data-id^="${quickOrderListSectionId}"]`
        );
      }

      get sectionId() {
        return (
          this.dataset.originalSection ||
          this.dataset.section
        );
      }
    }
  );
}